import React, { useState, useEffect, useRef, useMemo } from "react";
import { useHistory, useParams } from "react-router";
import { useSelector } from "react-redux";
import MonakoEditor, { useMonaco } from "@monaco-editor/react";

import "../../../../../builder/theme-layout/header/style.scss";
import "../cards/update/index.scss";

import { Button, LoaderNew as SystemLoader, Spin2 } from "../../../../../../components";

import { applySchemeDefinitions, MODEL_TYPES } from "../../../../../builder/screens/code/monacoSchemeDefinitions";
import { useThemesContext } from "../../../../../builder/context/ThemeProvider";

import {
  CSSIcon,
  FILEIcon,
  FONTIcon,
  HTMLIcon,
  IMAGEIcon,
  JSIcon,
  JSONIcon,
  NUNIcon
} from "../../../../../builder/screens/code/ThemeMenu";

const getExtensionIcon = (filename) => {
  let fileExt = filename?.split?.('.')?.pop?.();

  switch (fileExt) {
    case "png":
    case "jpg":
    case "jpeg":
    case "svg":
    case "gif":
    case "ico":
      return <HTMLIcon />;
    case "html":
      return <IMAGEIcon />;
    case "json":
      return <JSONIcon />;
    case "njk":
      return <NUNIcon />;
    case "js":
      return <JSIcon />;
    case "css":
      return <CSSIcon />;
    case "woff2":
    case "woff":
    case "ttf":
    case "eot":
      return <FONTIcon />;
    default:
      return <FILEIcon />;
  }
}

const AlertIcon = ({ color = '#D24242' }) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M10 7.5V9.16667M10 12.5V12.5083" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  <path d="M4.16666 15.8333H15.8333C16.1053 15.8314 16.3726 15.763 16.612 15.6341C16.8514 15.5051 17.0556 15.3195 17.2068 15.0935C17.3581 14.8675 17.4517 14.608 17.4795 14.3375C17.5074 14.067 17.4686 13.7937 17.3667 13.5417L11.45 3.33333C11.3059 3.07283 11.0946 2.85569 10.8381 2.70449C10.5817 2.55328 10.2894 2.47354 9.99166 2.47354C9.69395 2.47354 9.40166 2.55328 9.1452 2.70449C8.88874 2.85569 8.67746 3.07283 8.53333 3.33333L2.61666 13.5417C2.51661 13.788 2.47694 14.0547 2.50095 14.3194C2.52497 14.5842 2.61197 14.8394 2.75471 15.0637C2.89744 15.288 3.09175 15.4749 3.32143 15.6087C3.55111 15.7426 3.80949 15.8196 4.075 15.8333" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>

const ToolIcon = ({ color = 'var(--white)' }) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M5.83329 8.33332H8.33329V5.83332L5.41663 2.91666C6.34965 2.47105 7.39788 2.32566 8.41696 2.50051C9.43604 2.67536 10.3759 3.16184 11.107 3.89297C11.8381 4.6241 12.3246 5.56391 12.4994 6.58299C12.6743 7.60207 12.5289 8.6503 12.0833 9.58332L17.0833 14.5833C17.4148 14.9148 17.6011 15.3645 17.6011 15.8333C17.6011 16.3022 17.4148 16.7518 17.0833 17.0833C16.7518 17.4148 16.3021 17.6011 15.8333 17.6011C15.3645 17.6011 14.9148 17.4148 14.5833 17.0833L9.58329 12.0833C8.65027 12.5289 7.60204 12.6743 6.58296 12.4995C5.56388 12.3246 4.62407 11.8381 3.89294 11.107C3.16181 10.3759 2.67533 9.43607 2.50048 8.41699C2.32563 7.39791 2.47102 6.34968 2.91663 5.41666L5.83329 8.33332Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>

const CloseIcon = () => <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M13.5 4.5L4.5 13.5" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  <path d="M4.5 4.5L13.5 13.5" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>


function ConflictsResolution(props) {
  const { themeId } = useParams();
  const history = useHistory();
  const handleGoBack = () => {
    history.replace(history.location.state.prev);
  }
  const { getConflicts, loadingThemes, themes, resolveConflict, finishResolvingConflicts } = useThemesContext();
  const shopId = useSelector(({ auth }) => auth?.shop?.id);

  const [ conflicts, setConflicts ] = useState(null);
  const editor = useRef();
  const editorDecorations = useRef();
  const [ resolvedContent, setResolvedContent ] = useState(null);
  const [ activeConflict, setActiveConflict ] = useState(null);
  const [ isValid, setIsValid ] = useState(false);
  const theme = themes?.find?.(t => String(t.id) === themeId);
  const [ isLoading, setIsLoading ] = useState(!theme || !conflicts);

  const disable = isLoading || !isValid;
  const isDark = useSelector(({ theme }) => theme.globalTheme === 'dark');
  const monaco = useMonaco();

  useEffect(() => {
    monaco && applySchemeDefinitions(monaco);
  }, [monaco]);

  useEffect(() => {
    if (conflicts === null && theme && shopId) {
      getConflicts(theme).then(data => {
        setConflicts(data.conflicts);
        setIsLoading(false);
      });
    }
  }, [conflicts, theme, shopId]);

  const handleConflictSelect = (id) => {
    const c = conflicts.find(c => c.id === id);
    setActiveConflict(c);
    setResolvedContent(c.merge_conflicts);
    setTimeout(() => applyEditorDecorations(c.merge_conflicts), 500);
  }

  const handleResolveActiveConflict = async () => {
    setIsLoading(true);
    await resolveConflict(theme, activeConflict.id, resolvedContent);
    if (conflicts.length === 1) {
      await finishResolvingConflicts(theme);
      setConflicts([]);
      setActiveConflict(null);
      handleGoBack();
    } else {
      setConflicts(prev => prev.filter(p => p.id !== activeConflict.id));
      setActiveConflict(null);
    }
    setIsLoading(false);
  };

  const getEditorMode = (name, isSettings) => {
    const fileExtension = name?.split?.('.')?.pop?.();

    switch (true) {
      case fileExtension === 'settings' || fileExtension === 'json' || isSettings:
        return 'json';
      case fileExtension === 'njk':
        return 'twig';
      case fileExtension === 'css':
        return 'css';
      case fileExtension === 'js':
        return 'javascript';
      default:
        return 'twig';
    }
  };
  const language = useMemo(() => activeConflict ? getEditorMode(activeConflict?.entity_data?.file?.name, activeConflict.entity_conflict === 'settings') : '', [activeConflict]);
  const model = useMemo(() => {
    if (language !== "json") {
      return;
    }
    switch (activeConflict?.entity_data?.file?.entityType) {
      case "Layouts":
        return MODEL_TYPES.LAYOUT_SETTINGS;
      case "Templates":
        return MODEL_TYPES.TEMPLATE_SETTINGS;
      case "Sections":
        return MODEL_TYPES.SECTION_SETTINGS;
      case "Blocks":
        return MODEL_TYPES.BLOCK_SETTINGS;
      case "Settings":
        return MODEL_TYPES.THEME_SETTINGS;
      default:
        return;
    }
  }, [activeConflict, language]);

  const applyEditorDecorations = (content) => {
    try {
      const deltaDecorations = content.split("\n").reduce((acc, it, idx) => {
        if (/={7}/.test(it)) {
          acc.push({ line: idx -1, side: 'left' });
        }
        if (/<{7}|>{7}|={7}/.test(it)) {
          acc.push({ line: idx, side: />{7}/.test(it) ? 'right': 'left' });
        }
        return acc;
      }, []).reduce((acc, it, idx, arr) => {
        if (idx % 2 === 0) {
          return acc;
        }
        acc.push([arr[idx - 1], it]);
        return acc;
      }, []).map(lines => {
        return {
          range: new monaco.Range(
            lines[0].line + 1,
            0,
            lines[1].line + 1,
            0
          ),
          options: {
            isWholeLine: true,
            minimap: { color: lines[1].side === 'left' ? (isDark ? 'var(--red8)': 'var(--red1)') : (isDark ? 'var(--purple9)' : 'var(--purple1)'), position: 2 },
            className: `${lines[1].side}LineDecoration${isDark ? ' dark': ''}`,
            marginClassName: `${lines[1].side}LineDecoration${isDark ? ' dark': ''}`
          },
        };
      });

      editorDecorations.current = editor.current.deltaDecorations(
        editorDecorations.current || [],
        deltaDecorations
      );

      if (language === 'twig') {
        setIsValid(true);
      }
    } catch (e) {
      console.error(e);
    }
  }
  const handleValidate = markers => {
    setIsValid(!markers.filter(m => m.severity > 1).length);
  };

  const handleResolvedContentChange = (content) => {
    setResolvedContent(content);
    applyEditorDecorations(content);
  };

  if (loadingThemes) {
    return <SystemLoader />;
  }

  return (
    <>
      <div className="editor-header">
        <div>
          <div className="editor-header-logo conflicts-header">
            <AlertIcon />
            <div>
              Your theme has conflicts
            </div>
          </div>

          <div className="editor-header-nav">
            <div className="editor-header-menu conflicts-footer">
              <Button className="resolve" size="large" disabled={disable} onClick={handleResolveActiveConflict}>
                {isLoading ? <Spin2 /> :
                  <>
                    <ToolIcon color={!isValid ? 'var(--black3)': undefined} />
                    Resolve
                  </>
                }
              </Button>
              <Button size="large" square variant="secondary" onClick={handleGoBack}><CloseIcon /></Button>
            </div>
          </div>
        </div>
      </div>

      <div className="update-theme-body conflicts-body mb-0 min-vw-100">
        <div className="conflicts-list">
          {conflicts?.map?.(conflict => {
              let filename = conflict.entity_data?.file?.name?.replace('njk', conflict.entity_conflict === 'settings' ? 'json' : 'njk');

              if (conflict.entity_data.file === false && conflict.entity_conflict === 'settings') {
                filename = 'theme.json';
              }

              return (
                <div
                  key={conflict.id}
                  className={`conflicts-list-item ${conflict.id === activeConflict?.id ? 'active' : ''}`}
                  onClick={() => handleConflictSelect(conflict.id)}
                >
                  {getExtensionIcon(filename)}
                  {conflict.entity}/{filename}
                </div>
              );
            }
          )}
        </div>
        <div className="conflicts-content">
          {activeConflict ?
            <>
              <div>
                <MonakoEditor
                  onMount={_editor => (editor.current = _editor)}
                  value={resolvedContent}
                  language={language}
                  path={model}
                  onValidate={handleValidate}
                  onChange={handleResolvedContentChange}
                  theme={isDark ? 'vs-dark' : 'light'}
                />
              </div>
            </>
            : null}
        </div>
      </div>
    </>
  );
}

export default ConflictsResolution;
